import React, { useEffect, useMemo, useState } from "react";

import {
  MultiSelectProps,
  Option,
  OptionGroup,
  Options,
  Value
} from "../types";
import useSelectAll, { SelectAllOption } from "./useSelectAll";
import { flattenOptions, isOptionsGroup } from "./utils";

type Args = {
  options: Options;
  value?: Value;
  onChange: (value: Value, actionMeta?: any) => void;
  withSelectAll: boolean;
  isDisabled?: boolean;
  fixedValues: Array<string>;
  multiSelectRef: any;
  maxMultiValueLabels: number;
  disableAllSelected: boolean;
};

export default function useGetMultiSelectProps({
  value = [],
  options = [],
  onChange,
  withSelectAll,
  isDisabled,
  fixedValues,
  multiSelectRef,
  disableAllSelected
}: Args): MultiSelectProps {
  const [menuIsOpen, setMenuOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const onChangeSearch = (e: React.FormEvent<HTMLInputElement>) => {
    setSearchValue(e.currentTarget.value);
  };

  const onClickMenu = () => {
    if (!isDisabled) {
      setMenuOpen(open => !open);
    }
  };

  const removeItem = (removedValue: string) => {
    const newValue = value.filter(val => val.value !== removedValue);
    onChange(newValue, { removedValue });
  };

  const fixedOptions: readonly Option[] = useMemo(
    () => flattenOptions(options).filter(o => fixedValues.includes(o.value)),
    [options, fixedValues]
  );

  const removeAll = () => {
    onChange(fixedOptions);
  };

  const {
    optionsWithSelectAll,
    valuesWithSelectAll,
    isOptionSelected,
    onChangeWithSelectAll
  } = useSelectAll(options, value, onChange, fixedValues);

  const selectProps = {
    value: withSelectAll ? valuesWithSelectAll : value,
    options: withSelectAll ? optionsWithSelectAll : options,
    onChange: withSelectAll ? onChangeWithSelectAll : onChange,
    isOptionSelected: withSelectAll ? isOptionSelected : undefined
  };

  if (fixedValues.length) {
    selectProps.options = filterOptions(
      selectProps.options,
      option =>
        !fixedValues.find((fixedVal: string) => fixedVal === option.value)
    );
    selectProps.value = mergeFixedValues(selectProps.value, fixedOptions);
  }

  if (searchValue.length) {
    const searchResult = filterOptions(
      selectProps.options,
      option =>
        option.label !== SelectAllOption.label &&
        option.label.toLowerCase().startsWith(searchValue.toLowerCase())
    );
    selectProps.options = searchResult;
  }

  useEffect(() => {
    if (!menuIsOpen) {
      setSearchValue("");
    }
  }, [menuIsOpen]);

  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      if (!multiSelectRef?.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    function handleFocusOut(event: FocusEvent) {
      if (!multiSelectRef?.current?.contains(event.relatedTarget)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("focusout", handleFocusOut);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, [multiSelectRef]);

  return {
    removeItem,
    removeAll,
    onClickMenu,
    menuIsOpen,
    onChangeSearch,
    searchValue,
    allSelected: value.length === options.length,
    disableAllSelected,
    ...selectProps
  };
}

function filterOptions(
  options: Options,
  filterFunction: (optionOrGroup: Option) => boolean
) {
  return options.reduce(
    (filtered: Options, optionOrGroup: Option | OptionGroup) => {
      if (isOptionsGroup(optionOrGroup)) {
        const filteredGroup = optionOrGroup.options.filter(filterFunction);
        return [
          ...filtered,
          {
            ...optionOrGroup,
            options: filteredGroup
          }
        ];
      } else {
        return filterFunction(optionOrGroup)
          ? [...filtered, optionOrGroup]
          : filtered;
      }
    },
    []
  );
}

function mergeFixedValues(value: Value, fixedOptions: readonly Option[]) {
  const valuesWithFixed = [...value];
  fixedOptions.forEach(fixedOption => {
    if (!valuesWithFixed.map(v => v.value).includes(fixedOption.value)) {
      valuesWithFixed.push(fixedOption);
    }
  });

  return valuesWithFixed;
}
